import React, { useRef, useMemo } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';

// Shader for glass distortion effect
const GlassShader = {
  vertexShader: `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
  fragmentShader: `
    uniform sampler2D tBackground;
    uniform vec2 resolution;
    uniform float displace;
    uniform float distortionScale;
    uniform float redOffset;
    uniform float greenOffset;
    uniform float blueOffset;
    uniform float brightness;
    uniform float opacity;
    uniform float time;
    varying vec2 vUv;
    
    void main() {
      vec2 uv = vUv;
      
      // Create subtle wave distortion
      float wave = sin(uv.x * 10.0 + time * 0.5) * cos(uv.y * 8.0 + time * 0.3);
      vec2 distort = vec2(wave * displace * 0.001);
      
      // Chromatic aberration offsets
      vec2 rOffset = distort + vec2(redOffset * 0.0001, 0.0);
      vec2 gOffset = distort + vec2(0.0, greenOffset * 0.0001);
      vec2 bOffset = distort + vec2(blueOffset * 0.0001, blueOffset * 0.0001);
      
      // Sample with offsets for chromatic aberration
      float r = 0.0;
      float g = 0.0;
      float b = 0.0;
      
      // Glass tint color (dark with slight blue)
      vec3 glassTint = vec3(0.08, 0.09, 0.12);
      
      // Frosted glass effect - blur simulation
      vec3 color = glassTint;
      
      // Add subtle gradient
      float gradient = smoothstep(0.0, 1.0, uv.y);
      color += vec3(0.02) * gradient;
      
      // Add edge highlight
      float edge = smoothstep(0.0, 0.1, uv.x) * smoothstep(1.0, 0.9, uv.x);
      edge *= smoothstep(0.0, 0.1, uv.y) * smoothstep(1.0, 0.9, uv.y);
      color += vec3(0.05) * (1.0 - edge);
      
      // Apply brightness
      color *= brightness * 0.02;
      
      gl_FragColor = vec4(color, opacity);
    }
  `
};

function GlassPlane({ 
  width, 
  height, 
  borderRadius,
  displace,
  distortionScale,
  redOffset,
  greenOffset,
  blueOffset,
  brightness,
  opacity 
}) {
  const meshRef = useRef();
  const { size } = useThree();
  
  const uniforms = useMemo(() => ({
    tBackground: { value: null },
    resolution: { value: new THREE.Vector2(size.width, size.height) },
    displace: { value: displace },
    distortionScale: { value: distortionScale },
    redOffset: { value: redOffset },
    greenOffset: { value: greenOffset },
    blueOffset: { value: blueOffset },
    brightness: { value: brightness },
    opacity: { value: opacity },
    time: { value: 0 }
  }), [displace, distortionScale, redOffset, greenOffset, blueOffset, brightness, opacity, size]);
  
  useFrame((state) => {
    if (meshRef.current) {
      meshRef.current.material.uniforms.time.value = state.clock.elapsedTime;
    }
  });
  
  // Create rounded rectangle shape
  const shape = useMemo(() => {
    const s = new THREE.Shape();
    const r = Math.min(borderRadius, Math.min(width, height) / 2);
    const w = width;
    const h = height;
    
    s.moveTo(-w/2 + r, -h/2);
    s.lineTo(w/2 - r, -h/2);
    s.quadraticCurveTo(w/2, -h/2, w/2, -h/2 + r);
    s.lineTo(w/2, h/2 - r);
    s.quadraticCurveTo(w/2, h/2, w/2 - r, h/2);
    s.lineTo(-w/2 + r, h/2);
    s.quadraticCurveTo(-w/2, h/2, -w/2, h/2 - r);
    s.lineTo(-w/2, -h/2 + r);
    s.quadraticCurveTo(-w/2, -h/2, -w/2 + r, -h/2);
    
    return s;
  }, [width, height, borderRadius]);
  
  return (
    <mesh ref={meshRef}>
      <shapeGeometry args={[shape]} />
      <shaderMaterial
        vertexShader={GlassShader.vertexShader}
        fragmentShader={GlassShader.fragmentShader}
        uniforms={uniforms}
        transparent={true}
        blending={THREE.NormalBlending}
      />
    </mesh>
  );
}

export default function GlassSurface({
  children,
  width = 300,
  height = 200,
  borderRadius = 24,
  displace = 10,
  distortionScale = -100,
  redOffset = 5,
  greenOffset = 10,
  blueOffset = 15,
  brightness = 50,
  opacity = 0.85,
  mixBlendMode = 'normal',
  className = '',
  style = {}
}) {
  // Convert pixel dimensions to Three.js units (roughly 1:100 ratio)
  const scaleWidth = width / 100;
  const scaleHeight = height / 100;
  const scaleBorderRadius = borderRadius / 100;
  
  return (
    <div 
      className={`relative ${className}`}
      style={{ 
        width, 
        height, 
        mixBlendMode,
        ...style 
      }}
    >
      {/* WebGL Glass Background */}
      <div className="absolute inset-0 overflow-hidden" style={{ borderRadius }}>
        <Canvas
          camera={{ position: [0, 0, 5], fov: 50 }}
          style={{ background: 'transparent' }}
          gl={{ alpha: true, antialias: true }}
        >
          <GlassPlane
            width={scaleWidth}
            height={scaleHeight}
            borderRadius={scaleBorderRadius}
            displace={displace}
            distortionScale={distortionScale}
            redOffset={redOffset}
            greenOffset={greenOffset}
            blueOffset={blueOffset}
            brightness={brightness}
            opacity={opacity}
          />
        </Canvas>
      </div>
      
      {/* CSS Glass Fallback/Enhancement */}
      <div 
        className="absolute inset-0 backdrop-blur-md"
        style={{ 
          borderRadius,
          background: 'linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)',
          border: '1px solid rgba(255,255,255,0.08)',
          boxShadow: 'inset 0 1px 1px rgba(255,255,255,0.05), 0 4px 20px rgba(0,0,0,0.3)'
        }}
      />
      
      {/* Content */}
      <div className="relative z-10 w-full h-full flex items-center">
        {children}
      </div>
    </div>
  );
}
